import { Component, Input, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { UrlSafePipe, DataDmAttributeDirective } from '@boels/helpers';
import { BoelsVariantOption, BOProduct, CmsComponent } from '@boels/models';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  TRANSLOCO_SCOPE,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { map, Observable, of, switchMap } from 'rxjs';
import { ProductVariantSelectorListItemComponent } from './product-variant-selector-list-item/product-variant-selector-list-item.component';
import { CmsRouterLinkDirective } from '@boels/shared-state';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'boels-variant-selector',
  templateUrl: 'product-variant-selector.component.html',
  styleUrls: ['product-variant-selector.component.scss'],
  imports: [
    TranslocoModule,
    NgSelectModule,
    FormsModule,
    ProductVariantSelectorListItemComponent,
    CmsRouterLinkDirective,
    DataDmAttributeDirective,
    AsyncPipe,
  ],
  providers: [
    UrlSafePipe,
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['productDetails'],
    },
  ],
  host: { ngSkipHydration: 'true' },
})
export class ProductVariantSelectorComponent implements OnInit {
  private router = inject(Router);
  private translocoService = inject(TranslocoService);
  private urlSafePipe = inject(UrlSafePipe);

  @Input()
  componentData: {
    product$: Observable<BOProduct>;
    data$: Observable<CmsComponent>;
  };

  productData$: Observable<BOProduct>;

  selectedVariant: BoelsVariantOption;

  ngOnInit(): void {
    this.productData$ = this.updateProduct();
  }

  private updateProduct(): Observable<BOProduct> {
    return this.componentData.product$.pipe(
      switchMap((product: BOProduct) => {
        if (product.baseOptions && product.baseOptions?.length > 0) {
          return this.translocoService
            .selectTranslate('rental', {}, { scope: 'productDetails' })
            .pipe(
              map((rentalTranslation) => {
                // new data object to prevent read-only errors
                const newData: {
                  baseOptions: {
                    options: BoelsVariantOption[];
                    selected: BoelsVariantOption;
                  }[];
                } = {
                  baseOptions: [
                    {
                      options: [],
                      selected: {},
                    },
                  ],
                };

                // create selected option
                if (product.baseOptions && product.baseOptions[0]) {
                  const selected = product.baseOptions[0].selected;

                  const selectedUrl = this.router.createUrlTree([
                    rentalTranslation,
                    this.urlSafePipe.transform(
                      selected?.variantOptionQualifiers?.[0]?.value as string,
                    ),
                    'p',
                    selected?.code,
                  ]);

                  const newSelected = {
                    ...selected,
                    ...{ url: selectedUrl.toString() },
                  };
                  newData.baseOptions[0].selected = newSelected;

                  // create options
                  product.baseOptions[0].options?.forEach((variant, i) => {
                    // get variant name
                    const variantName =
                      variant.variantOptionQualifiers?.[0]?.value;

                    // build url
                    const url = this.router.createUrlTree([
                      rentalTranslation,
                      this.urlSafePipe.transform(variantName as string),
                      'p',
                      variant.code,
                    ]);

                    newData.baseOptions[0].options[i] = {
                      ...variant,
                      ...{ url: url.toString() },
                    };
                  });

                  this.selectedVariant = product.baseOptions[0]
                    ?.selected as BoelsVariantOption;
                }

                // finally update productData
                const productData: BOProduct = {
                  ...product,
                  ...newData,
                };

                return productData;
              }),
            );
        } else {
          return of();
        }
      }),
    );
  }
}
